import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography,
  TextField,
  Paper,
} from "@mui/material";
import * as Yup from "yup";
import { Formik } from "formik";
import API from "../../../axios/instances/API";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPCHA_SITE_KEY, RECAPCHA_ENABLED } from "src/storage/constants";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: fit-content;
`;

const Card = styled(Paper)`
  padding: 30px 40px 40px 40px;
  width: calc(min(520px, 96vw));
  min-height: 140px;

  @media (max-width: 560px) {
    padding: 20px;

    h2 {
      font-size: 22px;
    }
  }
`;

const TextLink = styled(Typography)`
  cursor: pointer;
  display: inline-block;
  color: ${(props) => props.theme.palette.info.light};

  &:hover {
    text-decoration: underline;
  }
`;

const initialValues = {
  name: "",
  email: "",
  message: "",
};

const validationSchema = Yup.object({
  name: Yup.string("Enter your Name")
    .min(1, "Name must be 1 characters long")
    .required("Name is required"),
  email: Yup.string("Enter your email")
    .email("Enter a valid email")
    .required("Email is required"),
  message: Yup.string()
    .min(8, "Message should be a minimum of 6 characters")
    .max(800, "Message can be at most 800 characters")
    .required("Message is required"),
});

const Contact = () => {
  const [msgSuccess, setMsgSuccess] = useState(false);
  const [msgError, setMsgError] = useState(false);
  const recaptchaRef = useRef(null);

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    setMsgSuccess(false);
    setMsgError(false);

    let recaptchaToken = null;
    try {
      if (RECAPCHA_ENABLED && !recaptchaRef.current) {
        throw new Error("Recapcha reference is null or undefined");
      }

      if (RECAPCHA_ENABLED) {
        recaptchaToken = await recaptchaRef.current.executeAsync();
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      }
    } catch (error) {
      setMsgError(true);
      return setSubmitting(false);
    }

    try {
      await API.post("dashboard/contact/", {
        name: values.name,
        email: values.email,
        message: values.message,
        "g-recaptcha-response": RECAPCHA_ENABLED ? recaptchaToken : "bypass",
      });
      setMsgSuccess(true);
      resetForm();
      setSubmitting(false);
    } catch (error) {
      setMsgError(true);
      setSubmitting(false);
      resetForm();
    }
  };

  return (
    <>
      <Helmet title="Spiffy | Contact Us" />
      <Wrapper>
        <Card>
          <Box>
            <Typography variant="h3">Contact Us</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Book time with a sales representative{" "}
              <em>
                <TextLink
                  target="_blank"
                  variant="subtitle2"
                  color="primary"
                  component="a"
                  href="https://meetings.hubspot.com/satch"
                >
                  here
                </TextLink>
              </em>
            </Typography>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">
              You can also email us at{" "}
              <em>
                <TextLink
                  variant="subtitle2"
                  color="primary"
                  component="a"
                  href="mailto: info@withspiffy.com"
                >
                  info@withspiffy.com
                </TextLink>
              </em>
            </Typography>
          </Box>
          <Box sx={{ mt: 4, mb: 1 }}>
            <Typography variant="h6">Or send us a message here</Typography>
          </Box>
          <Box>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleFormSubmit}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                errors,
                touched,
                isSubmitting,
              }) => (
                <form noValidate onSubmit={handleSubmit}>
                  <TextField
                    type="text"
                    name="name"
                    label="Your Name"
                    value={values.name}
                    sx={{ mb: 2, mt: 2 }}
                    fullWidth
                    error={Boolean(touched.name && errors.name)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.name && errors.name}
                    autoComplete="new-password"
                    variant="outlined"
                    margin="none"
                    size="small"
                  />

                  <TextField
                    type="email"
                    name="email"
                    label="Email Address"
                    value={values.email}
                    fullWidth
                    error={Boolean(touched.email && errors.email)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.email && errors.email}
                    autoComplete="new-password"
                    variant="outlined"
                    margin="none"
                    size="small"
                    sx={{ mb: 4, mt: 2 }}
                  />

                  <TextField
                    multiline
                    minRows={4}
                    maxRows={4}
                    type="password"
                    name="message"
                    label="Message"
                    value={values.message}
                    fullWidth
                    error={Boolean(touched.message && errors.message)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.message && errors.message}
                    autoComplete="new-password"
                    variant="outlined"
                  />
                  {RECAPCHA_ENABLED && (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPCHA_SITE_KEY}
                      size="invisible"
                      badge="bottomright"
                    />
                  )}
                  <Button
                    type="submit"
                    fullWidth
                    color="primary"
                    variant="contained"
                    sx={{ mt: 4 }}
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={20} />}
                  >
                    {isSubmitting ? "Submitting" : "Submit"}
                  </Button>
                </form>
              )}
            </Formik>
          </Box>
          {msgSuccess && (
            <Alert
              severity="success"
              sx={{ mt: 2 }}
              onClose={() => setMsgSuccess(false)}
            >
              Message Sent! We will get back to you shortly.
            </Alert>
          )}
          {msgError && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setMsgError(false)}
            >
              Hmm.. seems we have run into an error. Try again later.
            </Alert>
          )}
        </Card>
      </Wrapper>
    </>
  );
};

export default Contact;
